import { useCallback, useEffect, useRef, useState } from 'react';

export type PolledResult<T> = {
  data?: T;
  error?: Error;
  loading: boolean;
};

/**
 * Executa `fetcher` na montagem e depois a cada `intervalMs`.
 *
 * O polling pausa quando a aba está em segundo plano e dispara uma busca
 * imediata ao voltar — sem isso, cada aba aberta mantém tráfego constante
 * contra a API do Azure DevOps mesmo sem ninguém olhando.
 */
export function usePolled<T>(
  fetcher: () => Promise<T | undefined>,
  intervalMs: number,
): PolledResult<T> {
  const [result, setResult] = useState<PolledResult<T>>({ loading: true });

  // Mantém o fetcher mais recente sem reiniciar o timer a cada render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const mountedRef = useRef(true);

  const run = useCallback(async () => {
    try {
      const data = await fetcherRef.current();
      if (mountedRef.current) {
        setResult({ data, loading: false });
      }
    } catch (error) {
      if (mountedRef.current) {
        setResult({ error: error as Error, loading: false });
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        run();
      }
    };

    run();
    const timer = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);

    return () => {
      mountedRef.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [run, intervalMs]);

  return result;
}
